import { useCallback, useState } from 'react';

import { stripFilesRecursive } from '../utils/stripFiles';
import { ASSOCIATED_PERSONS_INITIAL_VALUES, AssociatedPersonsFormType } from './keys';

export interface UseAssociatedPersonSessionFormResult {
  sessionValues: AssociatedPersonsFormType;
  handleFormValueChange: (nextValues: AssociatedPersonsFormType) => void;
  setFormValues: (nextValues: AssociatedPersonsFormType) => void;
  setStateFormValues: (nextValues: AssociatedPersonsFormType) => void;
  resetSessionForm: () => void;
  refreshSessionValues: () => void;
  hasSessionData: () => boolean;
}

// Strip File objects from form data to prevent sessionStorage quota exceeded errors
// Files are replaced with metadata to indicate their presence
const stripFilesFromFormData = (data: AssociatedPersonsFormType): AssociatedPersonsFormType => {
  return stripFilesRecursive(data) as AssociatedPersonsFormType;
};

export function useAssociatedPersonSessionForm(
  storageKey: string,
  defaultValues: AssociatedPersonsFormType = ASSOCIATED_PERSONS_INITIAL_VALUES,
): UseAssociatedPersonSessionFormResult {
  const [sessionValues, setSessionValues] = useState<AssociatedPersonsFormType>(() => {
    const saved = sessionStorage.getItem(storageKey);
    return saved ? (JSON.parse(saved) as AssociatedPersonsFormType) : defaultValues;
  });

  // Use this during typing - only persists to sessionStorage, no re-render
  const handleFormValueChange = useCallback(
    (nextValues: AssociatedPersonsFormType) => {
      try {
        const dataWithoutFiles = stripFilesFromFormData(nextValues);
        const jsonString = JSON.stringify(dataWithoutFiles);
        sessionStorage.setItem(storageKey, jsonString);
      } catch (error) {
        // If storage quota is exceeded, silently fail but keep state in memory
        // Form data remains in component state for the current session
      }
    },
    [storageKey],
  );

  // Use this for programmatic updates - updates both storage and state
  const setFormValues = useCallback(
    (nextValues: AssociatedPersonsFormType) => {
      try {
        const dataWithoutFiles = stripFilesFromFormData(nextValues);
        const jsonString = JSON.stringify(dataWithoutFiles);
        sessionStorage.setItem(storageKey, jsonString);
      } catch (error) {
        // If storage quota is exceeded, silently fail but keep state in memory
        // Form data remains in component state for the current session
      }
      setSessionValues(nextValues);
    },
    [storageKey],
  );

  // Use this for programmatic updates - updates only state
  const setStateFormValues = useCallback((nextValues: AssociatedPersonsFormType) => {
    setSessionValues(nextValues);
  }, []);

  // Call this when modal opens to sync state with sessionStorage
  const refreshSessionValues = useCallback(() => {
    const saved = sessionStorage.getItem(storageKey);
    const freshValues = saved ? (JSON.parse(saved) as AssociatedPersonsFormType) : defaultValues;
    setSessionValues(freshValues);
  }, [storageKey, defaultValues]);

  const resetSessionForm = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setSessionValues(defaultValues);
  }, [storageKey, defaultValues]);

  const hasSessionData = useCallback(() => {
    return sessionStorage.getItem(storageKey) !== null;
  }, [storageKey]);

  return {
    sessionValues,
    handleFormValueChange,
    setFormValues,
    setStateFormValues,
    resetSessionForm,
    refreshSessionValues,
    hasSessionData,
  };
}
